import { createContext, useContext, useMemo } from "react";
import { MantineProvider } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";

import { getPortalTheme, type PortalTheme } from "@/api/branding";
import { useIAMAuth } from "@/auth/iam";
import { deriveInk, isHexColor } from "@/branding/color";
import { config } from "@/config";
import { labTheme, theme as medicusTheme } from "@/theme";

/**
 * The lab portal in the lab's own look (plan B5, D4).
 *
 * A lab user (a sign-in carrying an `organization_unit_id`) gets their organisation's name
 * and logo in the header and its primary and ink as the theme, resolved by the server from
 * the token, never from anything this app sends. Medicus staff keep the Medicus theme: the
 * server answers `{medicus: true}` for them, and a staff sign-in with no organisation is
 * never asked. Nothing is asked with SR_BRANDING_ENABLED off.
 *
 * **A failed read is the Medicus look, not an error.** The theme is decoration: a portal
 * that would not open because the branding store blinked would turn a cosmetic outage into
 * a clinical one (a doctor who cannot reach the approvals page).
 */

export interface LabLook {
  displayName: string | null;
  logo: { mediaType: string; dataUri: string } | null;
  /** Both set or both null: colours are one unit (D19). */
  primary: string | null;
  ink: string | null;
}

const LabLookContext = createContext<LabLook | null>(null);

/** The signed-in lab's look, or null for Medicus staff, unbranded labs and while loading. */
export function useLabLook(): LabLook | null {
  return useContext(LabLookContext);
}

/** The server's answer as the look the shell applies. Null means the Medicus look. */
export function labLookOf(answer: PortalTheme | undefined): LabLook | null {
  if (answer === undefined || "medicus" in answer) {
    return null;
  }
  const primary = answer.primary !== null && isHexColor(answer.primary) ? answer.primary : null;
  let ink: string | null = null;
  if (primary !== null) {
    ink = answer.ink !== null && isHexColor(answer.ink) ? answer.ink : deriveInk(primary);
  }
  // Only a data: URI of one of the two accepted types reaches an <img src>.
  const logo =
    answer.logo !== null &&
    /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/]+=*$/.test(answer.logo.data_uri)
      ? { mediaType: answer.logo.media_type, dataUri: answer.logo.data_uri }
      : null;
  const displayName = answer.display_name?.trim() ? answer.display_name : null;
  if (displayName === null && logo === null && primary === null) {
    return null;
  }
  return { displayName, logo, primary, ink };
}

export function usePortalThemeQuery(): LabLook | null {
  const { isAuthenticated, organizationId } = useIAMAuth();
  const enabled = config.brandingEnabled && isAuthenticated && Boolean(organizationId);
  const { data } = useQuery({
    queryKey: ["branding-theme", organizationId],
    queryFn: getPortalTheme,
    enabled,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
  return enabled ? labLookOf(data) : null;
}

/**
 * `MantineProvider` with the lab's theme for a lab user and the Medicus one otherwise.
 *
 * A lab with colours is shown light: its ink is checked for contrast against white, not
 * against the dark Medicus background, so the dark scheme could put the lab's own ink on a
 * background it was never checked on.
 */
export function BrandedMantineProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const look = usePortalThemeQuery();
  const primary = look?.primary ?? null;
  const ink = look?.ink ?? null;
  const branded = primary !== null && ink !== null;
  const activeTheme = useMemo(
    () => (primary !== null && ink !== null ? labTheme(primary, ink) : medicusTheme),
    [primary, ink],
  );
  return (
    <MantineProvider
      theme={activeTheme}
      defaultColorScheme="dark"
      {...(branded ? { forceColorScheme: "light" as const } : {})}
    >
      <LabLookContext.Provider value={look}>{children}</LabLookContext.Provider>
    </MantineProvider>
  );
}
